import {
  DateRangeIntervalWithoutWeek,
  InspectionEvolutionFilters,
} from 'shared/types/analytics';
import {
  isDateDifferenceInvalid,
  setMultiselectFilterValue,
} from 'charts/common/filters';
import { FilterAction } from './types';
import {
  FilterActionType,
  FilterActionTypeInterval,
  RewriteFiltersActionEnum,
} from 'charts/common/types';
import { showToast } from 'services/toaster';
import { TOASTER_TYPES } from 'components/core/Toaster/types';

type InspectionEvolutionReducer = (
  state: InspectionEvolutionFilters | undefined,
  action: FilterAction
) => InspectionEvolutionFilters | undefined;
export function createInspectionEvolutionReducer(
  timezone: string
): InspectionEvolutionReducer {
  const showTooLargeIntervalErrorToast = (): void => {
    showToast({
      type: TOASTER_TYPES.FAILURE,
      message: { id: 'date_range_interval_too_large_error' },
      toasterPosition: {
        vertical: 'bottom',
        horizontal: 'center',
      },
      hideDelay: 6000,
    });
  };

  return function filtersReducer(
    state: InspectionEvolutionFilters | undefined,
    action: FilterAction
  ): InspectionEvolutionFilters | undefined {
    if (action.type === RewriteFiltersActionEnum.rewriteFilters)
      return action.payload;
    if (!state) return undefined;

    switch (action.type) {
      case FilterActionTypeInterval.setDateInterval: {
        const [intervalFilter, dateRangeFilter, ...otherFilters] =
          state.dataScope.filters;
        const { from, to } = dateRangeFilter.value;

        if (isDateDifferenceInvalid(from, to, action.payload, timezone)) {
          showTooLargeIntervalErrorToast();
          return state;
        }

        return {
          ...state,
          dataScope: {
            ...state.dataScope,
            filters: [
              { ...intervalFilter, value: action.payload },
              dateRangeFilter,
              ...otherFilters,
            ],
          },
        };
      }

      case FilterActionType.setDateRange: {
        const { from, to } = action.payload;
        const [intervalFilter, dateRangeFilter, ...otherFilters] =
          state.dataScope.filters;
        const interval: DateRangeIntervalWithoutWeek = intervalFilter.value;
        if (isDateDifferenceInvalid(from, to, interval, timezone)) {
          showTooLargeIntervalErrorToast();
          return state;
        }

        return {
          ...state,
          dataScope: {
            ...state.dataScope,
            filters: [
              intervalFilter,
              {
                ...dateRangeFilter,
                value: {
                  from: action.payload.from,
                  to: action.payload.to,
                },
              },
              ...otherFilters,
            ],
          },
        };
      }

      case FilterActionType.setProcesses:
        return setMultiselectFilterValue(state, 2, action.payload);

      case FilterActionType.setSites:
        return setMultiselectFilterValue(state, 3, action.payload);

      case FilterActionType.setManagers:
        return setMultiselectFilterValue(state, 4, action.payload);
      default:
        const exhaustiveCheck = action as never;
        throw new Error(
          `Unhandled filters reducer case: ${(exhaustiveCheck as { type: string }).type}`
        );
    }
  };
}
